/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2022 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
**************************************************************************/
/* eslint-disable no-underscore-dangle */

/**
 * Node for doubly linked list.
 */
export class DoublyLinkedNode {
    constructor(value) {
        this._value = value;
        this._previousNode = null;
        this._nextNode = null;
    }

    get value() {
        return this._value;
    }

    get previousNode() {
        return this._previousNode;
    }

    set previousNode(node) {
        this._previousNode = node;
    }

    get nextNode() {
        return this._nextNode;
    }

    set nextNode(node) {
        this._nextNode = node;
    }

    /**
     * De-links the node from list.
     */
    unlink() {
        this._previousNode = null;
        this._nextNode = null;
    }
}

/**
 * Doubly linked list implementation.
 */
export class DoublyLinkedList {
    constructor() {
        this._headNode = null; // first node in list
        this._tailNode = null; // last node in list
        this._countNodes = 0;
    }

    get headNode() {
        return this._headNode;
    }

    get tailNode() {
        return this._tailNode;
    }

    /**
     * Number of nodes in list.
     */
    nodeCount() {
        return this._countNodes;
    }

    /**
     * Appends {@link DoublyLinkedNode} to the end of list.
     * @param {DoublyLinkedNode} node - Node to add.
     */
    append(node) {
        if (!(node instanceof DoublyLinkedNode)) {
            return;
        }

        if (this._countNodes === 0) {
            this._headNode = node;
            this._tailNode = node;
        } else {
            this._tailNode.nextNode = node;
            node.previousNode = this._tailNode;
            this._tailNode = node;
        }
        this._countNodes += 1;
    }

    /**
     * This utility is used by LruCache.
     * Brings {@link DoublyLinkedNode} to the front of list.
     * @param {DoublyLinkedNode} node - Node to update.
     */
    moveToFront(node) {
        if (!(node instanceof DoublyLinkedNode) || (this._headNode === node)) {
            return;
        }

        if (!this.remove(node)) { // node to update is not part of the list
            return;
        }

        node.nextNode = this._headNode;
        this._headNode.previousNode = node;
        this._headNode = node;
        this._countNodes += 1; // removing the node decreases the node count
    }

    /**
     * Removes {@link DoublyLinkedNode} from list.
     * @param {DoublyLinkedNode} node - Node to erase.
     */
    remove(node) {
        if (!(node instanceof DoublyLinkedNode)) {
            return null;
        }

        if (this._countNodes === 1 && this._headNode === node) {
            this._tailNode = null;
            this._headNode = null;
        } else if (node === this._headNode) {
            this._headNode = this._headNode.nextNode;
            this._headNode.previousNode = null;
        } else if (node === this._tailNode) {
            this._tailNode = this._tailNode.previousNode;
            this._tailNode.nextNode = null;
        } else if (node.nextNode && node.previousNode) {
            // !!! Ideally we should check if 'node' to be
            // deleted is a part of this doubly linked list
            // instance which has '_headNode' as its head !!!
            node.previousNode.nextNode = node.nextNode;
            node.nextNode.previousNode = node.previousNode;
        } else { // node is not part of the list
            return null;
        }
        node.unlink();
        this._countNodes -= 1;
        return node;
    }

    /**
     * Removes the node at front of list.
     * @returns the front node.
     */
    front() {
        if (this.isEmpty()) {
            return null;
        }
        return this.remove(this._headNode);
    }

    /**
     * Checks if list is empty.
     */
    isEmpty() {
        return this._headNode === null;
    }
}
